import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Archive, Download, Eye, Loader2, Search, UserCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, downloadFile, fmtDate } from "@/lib/api";
import { fmtDob } from "@/components/admin/DowReviewerTools";
import { usePager, MoreRows } from "@/components/admin/DowColumns";

const plus5 = (d: string) => (d ? `${Number(d.slice(0, 4)) + 5}${d.slice(4)}` : "");
const today = () => new Date().toISOString().slice(0, 10);

function FixRow({ r, onSaved }: { r: any; onSaved: () => void }) {
  const [issued, setIssued] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/dow/clearances/${r.id}/dates`, { issued, expires: "" }); toast.success(`Dates fixed for ${r.name}`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <tr data-testid={`dow-future-row-${r.id}`}>
      <td className="font-semibold">{r.first_name} {r.last_name}<p className="font-mono text-[11px] font-normal text-[#94A3B8]">{r.number}</p></td>
      <td className="font-mono text-xs">{fmtDob(r.dob)}</td>
      <td className="text-xs">{r.location || "\u2014"}</td>
      <td className="font-mono text-xs text-[#B91C1C]">{fmtDate(r.issued_at)}</td>
      <td><div className="flex items-center gap-2"><input type="date" max={today()} className="field-input h-9 w-40 text-sm" value={issued} onChange={(e) => setIssued(e.target.value)} data-testid={`dow-future-date-${r.id}`} />
        <button className="btn-primary btn-sm" disabled={!issued || busy} onClick={save} data-testid={`dow-future-save-${r.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</button></div>
        {issued && <p className="mt-1 text-[11px] text-[#64748B]">Expires {fmtDate(plus5(issued) + "T12:00:00")}</p>}</td>
    </tr>
  );
}

export function FutureDates({ refresh, onChanged }: { refresh: number; onChanged: () => void }) {
  const [rows, setRows] = useState<any[]>([]);
  const load = () => api.get("/dow/clearances/future-dated").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refresh]);
  if (!rows.length) return null;
  return (
    <details className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="dow-future-dates" open>
      <summary className="cursor-pointer text-sm font-semibold text-[#92400E]"><AlertTriangle className="mr-1 inline h-4 w-4" /> {rows.length} clearance(s) have a clearance date in the future. These are typing or import errors: enter the correct date.</summary>
      <div className="mt-2 max-h-80 overflow-auto rounded-lg bg-white">
        <table className="tbl tbl-compact"><thead><tr><th>Name</th><th>DOB</th><th>Parish / school</th><th>Date on file</th><th>Correct clearance date</th></tr></thead>
          <tbody>{rows.map((r) => <FixRow key={r.id} r={r} onSaved={() => { load(); onChanged(); }} />)}</tbody></table>
      </div>
    </details>
  );
}

function NoCharterGroup({ g, charters, onDone }: { g: any; charters: any[]; onDone: () => void }) {
  const [ch, setCh] = useState(g.suggested || "");
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const assign = async () => {
    setBusy(true);
    try {
      const body = pick.size ? { charter: ch, ids: [...pick] } : { charter: ch, locations: g.variants.map((v: any) => v.text) };
      const { data } = await api.post("/dow/clearances/assign-charter", body);
      toast.success(`${data.updated} clearance(s) assigned to Charter ${data.charter} ${data.name}`); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const flip = (id: string) => setPick((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const tid = g.key.replace(/\s+/g, "-");
  const name = (n: string) => charters.find((c) => c.charter === n)?.name || "";
  return (
    <details className="rounded-lg border border-[#E2E8F0] bg-white p-3" onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)} data-testid={`dow-nocharter-group-${tid}`}>
      <summary className="flex cursor-pointer flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold">{g.location || "No parish / school entered"}</span>
        <span className="rounded-full bg-[#F1F5F9] px-2 py-0.5 text-xs">{g.count.toLocaleString()} {g.count === 1 ? "person" : "people"}</span>
        {g.variants.length > 1 && <span className="text-xs text-[#64748B]">{g.variants.length} spellings</span>}
        {g.suggested ? <span className="text-xs text-[#0F766E]">Suggested: Charter {g.suggested} {name(g.suggested)}</span>
          : g.options.length > 1 && <span className="text-xs text-[#B45309]">Could be: {g.options.map((n: string) => `${n} ${name(n)}`).join(" or ")}</span>}
      </summary>
      {open && <>
      {g.variants.length > 1 && <p className="mt-2 text-xs text-[#64748B]">Spellings in this group: {g.variants.map((v: any) => `"${v.text || "(blank)"}" (${v.count})`).join(", ")}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select className="field-input h-9 w-72 text-sm" value={ch} onChange={(e) => setCh(e.target.value)} data-testid={`dow-nocharter-select-${tid}`}>
          <option value="">Choose a charter…</option>
          {charters.map((c) => <option key={c.charter} value={c.charter}>{c.charter} · {c.name}</option>)}
        </select>
        {!g.suggested && g.options.map((n: string) => <button key={n} className="btn-secondary btn-sm" onClick={() => setCh(n)} data-testid={`dow-nocharter-option-${tid}-${n}`}>{n} {name(n)}</button>)}
        <button className="btn-primary btn-sm" disabled={!ch || busy} onClick={assign} data-testid={`dow-nocharter-assign-${tid}`}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {pick.size ? `Assign ${pick.size} selected` : `Assign all ${g.count.toLocaleString()}`}
        </button>
      </div>
      <p className="mt-1 text-[11px] text-[#64748B]">Tick people to assign only some of them (for example if this group mixes two parishes).</p>
      <ul className="mt-2 max-h-56 overflow-auto text-sm">{g.people.map((p: any) => (
        <li key={p.id} className="flex items-center gap-2 py-0.5"><input type="checkbox" className="h-4 w-4" checked={pick.has(p.id)} onChange={() => flip(p.id)} data-testid={`dow-nocharter-pick-${p.id}`} />
          <span>{p.last_name}, {p.first_name}</span><span className="font-mono text-xs text-[#94A3B8]">DOB {fmtDob(p.dob)} · cleared {fmtDate(p.issued_at)}{p.location !== g.location ? ` · typed "${p.location || ""}"` : ""}</span></li>
      ))}</ul>
      </>}
    </details>
  );
}

export function NoCharter({ refresh, onChanged }: { refresh: number; onChanged: () => void }) {
  const [groups, setGroups] = useState<any[]>([]);
  const [charters, setCharters] = useState<any[]>([]);
  const load = () => api.get("/dow/clearances/no-charter").then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refresh]);
  useEffect(() => { api.get("/dow/charters").then((r) => setCharters(r.data.filter((c: any) => !c.closed))).catch(() => {}); }, []);
  const total = groups.reduce((n, g) => n + g.count, 0);
  const sugg = groups.filter((g) => g.suggested);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(false);
  const [skip, setSkip] = useState<Set<string>>(new Set());
  const chName = (n: string) => charters.find((c) => c.charter === n)?.name || "";
  const assignSuggested = async () => {
    setBusy(true);
    let n = 0;
    try { for (const g of sugg.filter((x) => !skip.has(x.key))) { n += (await api.post("/dow/clearances/assign-charter", { charter: g.suggested, locations: g.variants.map((v: any) => v.text) })).data.updated; } toast.success(`${n.toLocaleString()} clearance(s) assigned to their suggested charter`); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false); setReview(false); setSkip(new Set()); load(); onChanged();
  };
  if (!total) return null;
  return (
    <details className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="dow-no-charter">
      <summary className="cursor-pointer text-sm font-semibold text-[#92400E]"><AlertTriangle className="mr-1 inline h-4 w-4" /> {total.toLocaleString()} clearance(s) have no charter number. Click to assign a charter. They don't appear on a charter's lists or reports until you do.</summary>
      <p className="mt-2 text-xs text-[#92400E]">Grouped by the parish / school name typed in Access (different spellings of the same name are grouped together). Pick the charter for each group and click Assign; the parish name is updated to the charter's name (the original text is kept on the record).</p>
      {sugg.length > 0 && !review && <button className="btn-primary btn-sm mt-2" onClick={() => setReview(true)} data-testid="dow-nocharter-review-suggested">Review {sugg.length} suggested matches ({sugg.reduce((n, g) => n + g.count, 0).toLocaleString()} people)</button>}
      {review && (
        <div className="mt-2 rounded-lg border border-[#BFDBFE] bg-white p-3" data-testid="dow-nocharter-review">
          <p className="text-sm font-semibold">Untick any suggestion that's wrong, then click Assign.</p>
          <ul className="mt-2 max-h-64 overflow-auto text-sm">{sugg.map((g) => (
            <li key={g.key} className="flex items-center gap-2 py-0.5"><input type="checkbox" className="h-4 w-4" checked={!skip.has(g.key)} onChange={() => setSkip((s) => { const n = new Set(s); n.has(g.key) ? n.delete(g.key) : n.add(g.key); return n; })} data-testid={`dow-nocharter-review-${g.key.replace(/\s+/g, "-")}`} />
              <span>"{g.location}"{g.variants.length > 1 ? ` + ${g.variants.length - 1} spelling(s)` : ""} ({g.count})</span><span className="text-[#94A3B8]">→</span><span className="font-semibold text-[#0F766E]">Charter {g.suggested} {chName(g.suggested)}</span></li>
          ))}</ul>
          <div className="mt-2 flex gap-2"><button className="btn-secondary btn-sm" onClick={() => setReview(false)}>Cancel</button>
            <button className="btn-primary btn-sm" disabled={busy || skip.size === sugg.length} onClick={assignSuggested} data-testid="dow-nocharter-assign-suggested">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Assign {sugg.filter((g) => !skip.has(g.key)).reduce((n, g) => n + g.count, 0).toLocaleString()} people</button></div>
        </div>
      )}
      <div className="mt-2 max-h-[28rem] space-y-2 overflow-auto">{groups.map((g) => <NoCharterGroup key={g.key} g={g} charters={charters} onDone={() => { load(); onChanged(); }} />)}</div>
    </details>
  );
}

const STATUS_TXT: Record<string, string> = { active: "Active", expiring: "Expiring soon", expired: "Expired" };

export function MatchBadge({ r, onChanged }: { r: any; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<string[]>([]);
  if (!r.matches?.length) return null;
  const archive = async (m: any) => {
    try {
      await api.post(`/dow/clearances/${m.id}/archive`, { on: true, note: `Re-applied: new application ${r.case_no || r.code || ""}`.trim() });
      setDone([...done, m.id]); toast.success(`${m.name}'s old clearance moved to Archived`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <button className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[11px] font-semibold text-[#92400E] hover:bg-[#FDE68A]" onClick={() => setOpen(true)} data-testid={`dow-match-badge-${r.id}`}>
        <UserCheck className="h-3 w-3" /> Possible match: {r.matches.length} existing clearance{r.matches.length > 1 ? "s" : ""}
      </button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o && done.length) onChanged(); }}>
        <DialogContent className="max-w-2xl" data-testid="dow-match-dialog">
          <DialogHeader><DialogTitle>Is this the same person?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#475569]">Matched on date of birth and first or last name. Compare the details. If this new application replaces an old clearance (for example a renewal), move the old record to Archived. Nothing moves automatically.</p>
          <div className="rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] p-3 text-sm" data-testid="dow-match-new">
            <p className="text-[11px] font-semibold uppercase text-[#1B365D]">New application</p>
            <p className="font-semibold">{r.first_name} {r.last_name} · DOB {fmtDob(r.dob)}</p>
            <p className="text-xs text-[#475569]">{r.address || "No address"} · {r.location || "No parish"}</p>
          </div>
          <div className="max-h-[45vh] space-y-2 overflow-auto">{r.matches.map((m: any) => (
            <div key={m.id} className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-[#E2E8F0] p-3 text-sm" data-testid={`dow-match-old-${m.id}`}>
              <div>
                <p className="font-semibold">{m.first_name} {m.last_name} · DOB {fmtDob(m.dob)}</p>
                <p className="text-xs text-[#475569]">{m.address || "No address"} · {m.location || "No parish"}{m.charter ? ` (Charter ${m.charter})` : ""}</p>
                <p className="text-xs text-[#64748B]">Cleared {fmtDate(m.issued_at)} · expires {m.expires_at ? fmtDate(m.expires_at) : "never"} · {STATUS_TXT[m.status]} · {m.number}</p>
              </div>
              {done.includes(m.id) ? <span className="text-xs font-semibold text-[#0F766E]">Moved to Archived</span>
                : <button className="btn-secondary btn-sm" onClick={() => archive(m)} data-testid={`dow-match-archive-${m.id}`}><Archive className="h-4 w-4" /> Move old record to Archived</button>}
            </div>
          ))}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function RevokeDialog({ r, onClose, onDone }: { r: any; onClose: () => void; onDone: () => void }) {
  const [date, setDate] = useState(today());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!r) return null;
  const go = async () => {
    setBusy(true);
    try { await api.post(`/dow/clearances/${r.id}/move-to-not-approved`, { date, reason }); toast.success(`${r.name} moved to Not approved. The original clearance is kept on their record.`); onClose(); onDone(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dow-revoke-dialog">
        <DialogHeader><DialogTitle>Move {r.name} to Not approved?</DialogTitle></DialogHeader>
        <p className="text-sm text-[#475569]">Their original clearance ({fmtDate(r.issued_at)}, {r.number}) stays on their record for documentation. No emails or notices are sent.</p>
        <label className="text-sm font-medium">Date the clearance was revoked<input type="date" max={today()} className="field-input mt-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dow-revoke-date" /></label>
        <label className="text-sm font-medium">Reason (private)<textarea className="field-input mt-1 min-h-[70px]" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dow-revoke-reason" /></label>
        <div className="flex justify-end gap-2"><button className="btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm bg-[#B91C1C]" disabled={busy || !date} onClick={go} data-testid="dow-revoke-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Move to Not approved</button></div>
      </DialogContent>
    </Dialog>
  );
}

export function RestoreDialog({ r, onClose, onDone }: { r: any; onClose: () => void; onDone: () => void }) {
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  if (!r) return null;
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/dow/legacy-not-cleared/${r.id}/move-to-cleared`, { date: r.decided_at ? "" : date }); toast.success(`${r.name} moved to Clearances: cleared ${fmtDate(data.issued_at)}, expires ${fmtDate(data.expires_at)}`); onClose(); onDone(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dow-restore-dialog">
        <DialogHeader><DialogTitle>Move {r.name} to Clearances?</DialogTitle></DialogHeader>
        {r.decided_at ? <p className="text-sm text-[#475569]">Their clearance date will be <b>{fmtDate(r.decided_at)}</b> (the date on this record), expiring 5 years later. The Not approved record is kept on their file.</p>
          : <label className="text-sm font-medium">This record has no date. Clearance date to use<input type="date" max={today()} className="field-input mt-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dow-restore-date" /></label>}
        <div className="flex justify-end gap-2"><button className="btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy || (!r.decided_at && !date)} onClick={go} data-testid="dow-restore-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Move to Clearances</button></div>
      </DialogContent>
    </Dialog>
  );
}

export function RosterButtons({ charter }: { charter: string }) {
  const url = (f: string) => `/dow/charters/${encodeURIComponent(charter)}/roster?fmt=${f}`;
  const day = new Date().toISOString().slice(0, 10);
  return (
    <>
      <button className="btn-secondary btn-sm whitespace-nowrap" title="View this charter's full All Cleared list (PDF)" onClick={() => downloadFile(url("pdf"), `Charter-${charter}-All-Cleared-${day}.pdf`).catch((e) => toast.error(errMsg(e)))} data-testid={`dow-roster-pdf-${charter}`}><Eye className="h-4 w-4" /> All cleared</button>
      <button className="btn-secondary btn-sm px-2" title="Download the full All Cleared list as Excel" onClick={() => downloadFile(url("xlsx"), `Charter-${charter}-All-Cleared-${day}.xlsx`).catch((e) => toast.error(errMsg(e)))} data-testid={`dow-roster-xlsx-${charter}`}><Download className="h-4 w-4" /> Excel</button>
    </>
  );
}

export function UsedCodes() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/dow/used-codes").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const list = (rows || []).filter((r) => { const t = `${r.code} ${r.charter} ${r.charter_name} ${r.first_name} ${r.last_name}`.toLowerCase(); return words.every((w) => t.includes(w)); });
  const pg = usePager(list, q);
  if (!rows) return <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-[#94A3B8]" />;
  return (
    <div className="space-y-3 p-4" data-testid="dow-used-codes">
      <div className="relative max-w-sm"><Search className="absolute left-3 top-3 h-4 w-4 text-[#94A3B8]" />
        <input className="field-input pl-9" placeholder="Search code, charter or name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dow-used-codes-search" /></div>
      <p className="text-xs text-[#64748B]" data-testid="dow-used-codes-count">{list.length.toLocaleString()} of {rows.length.toLocaleString()} used entry codes</p>
      <div className="max-h-[60vh] overflow-auto rounded-lg border border-slate-100">
        <table className="tbl tbl-compact" data-testid="dow-used-codes-table">
          <thead><tr><th>Entry code</th><th>Charter</th><th>Date used</th><th>First name</th><th>Last name</th></tr></thead>
          <tbody>{pg.rows.map((r: any, i: number) => (
            <tr key={`${r.code}-${i}`} data-testid={`dow-used-code-${r.code}`}>
              <td className="font-mono text-xs font-semibold">{r.code}</td>
              <td className="text-sm"><span className="font-mono text-xs text-[#64748B]">{r.charter}</span> {r.charter_name}</td>
              <td className="whitespace-nowrap font-mono text-xs">{fmtDate(r.used_at)}</td>
              <td>{r.first_name || "\u2014"}</td><td>{r.last_name || "\u2014"}</td>
            </tr>
          ))}</tbody>
        </table>
        <MoreRows p={pg} testId="used-codes" />
      </div>
    </div>
  );
}
